import { useState, useEffect } from 'react';
import { supabase } from './supabase';

export type DbOrganization = {
  id: string;
  organization_code?: string;
  organization_name: string;
  verified_domain?: string | null;
  email?: string;
  phone?: string;
  contact?: string;
  gstin?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  timezone?: string;
  currency?: string;
  logo_url?: string;
  status?: string;
  created_by?: string;
  created_at: string;
  updated_at?: string;
  domain_verified?: boolean;
  verification_status?: string;
  verification_method?: string;
  http_status?: number;
  dns_resolved?: boolean;
  ssl_valid?: boolean;
  verified_at?: string;
  last_verified_at?: string;
  verification_message?: string;
};

export type DbDepartment = {
  id: string;
  organization_id: string;
  name: string;
  code?: string | null;
  description?: string | null;
  head_id?: string | null;
  status?: string;
  created_at: string;
};

export type DbTeam = {
  id: string;
  organization_id: string;
  department_id: string | null;
  name: string;
  code?: string | null;
  description?: string | null;
  lead_id?: string | null;
  default_role?: string | null;
  sla_policy?: string | null;
  status?: string;
  created_at: string;
};

export function useOrganizationHierarchy() {
  const [organizations, setOrganizations] = useState<DbOrganization[]>([]);
  const [departments, setDepartments] = useState<DbDepartment[]>([]);
  const [teams, setTeams] = useState<DbTeam[]>([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = async () => {
    try {
      const { data: { session } } = await supabase!.auth.getSession();
      return {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
      };
    } catch {
      return { 'Content-Type': 'application/json' };
    }
  };

  const fetchHierarchy = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();

      // 1. Fetch Organizations
      try {
        const res = await fetch('/api/organizations', { headers });
        if (res.ok) {
          const orgs = await res.json();
          if (Array.isArray(orgs)) setOrganizations(orgs);
        } else if (supabase) {
          const { data: orgs } = await supabase.from('organizations').select('*');
          if (orgs) setOrganizations(orgs);
        }
      } catch {
        if (supabase) {
          const { data: orgs } = await supabase.from('organizations').select('*');
          if (orgs) setOrganizations(orgs);
        }
      }

      // 2. Fetch Departments
      try {
        const dRes = await fetch('/api/departments', { headers });
        if (dRes.ok) {
          const depts = await dRes.json();
          if (Array.isArray(depts)) setDepartments(depts);
        } else if (supabase) {
          const { data: depts } = await supabase.from('departments').select('*');
          if (depts) setDepartments(depts);
        }
      } catch {
        if (supabase) {
          const { data: depts } = await supabase.from('departments').select('*');
          if (depts) setDepartments(depts);
        }
      }

      // 3. Fetch Teams
      try {
        const tRes = await fetch('/api/teams?organizationId=ALL', { headers });
        if (tRes.ok) {
          const tms = await tRes.json();
          if (Array.isArray(tms)) setTeams(tms);
        } else if (supabase) {
          const { data: tms } = await supabase.from('teams').select('*');
          if (tms) setTeams(tms);
        }
      } catch {
        if (supabase) {
          const { data: tms } = await supabase.from('teams').select('*');
          if (tms) setTeams(tms);
        }
      }
    } catch (e) {
      console.error("Failed to fetch hierarchy from Supabase", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHierarchy();
  }, []);

  const addOrganization = async (orgData: Partial<DbOrganization>) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/organizations', {
        method: 'POST',
        headers,
        body: JSON.stringify(orgData)
      });
      if (res.ok) {
        const data = await res.json();
        await fetchHierarchy();
        return data;
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to create organization');
    } catch (err: any) {
      if (supabase) {
        const { data, error } = await supabase.from('organizations').insert(orgData).select().single();
        if (error) throw error;
        await fetchHierarchy();
        return data;
      }
      throw err;
    }
  };

  const updateOrganization = async (id: string, updates: Partial<DbOrganization>) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/organizations?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        const data = await res.json();
        await fetchHierarchy();
        return data;
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to update organization');
    } catch (err: any) {
      if (supabase) {
        const { data, error } = await supabase.from('organizations').update(updates).eq('id', id).select().single();
        if (error) throw error;
        await fetchHierarchy();
        return data;
      }
      throw err;
    }
  };

  const deleteOrganization = async (id: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/organizations?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        await fetchHierarchy();
        return;
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to delete organization');
    } catch (err: any) {
      if (supabase) {
        const { error, count } = await supabase.from('organizations').delete({ count: 'exact' }).eq('id', id);
        if (error) throw error;
        if (count === 0) throw new Error("Delete failed. You may not have permission or the organization doesn't exist.");
        await fetchHierarchy();
        return;
      }
      throw err;
    }
  };

  const verifyDomain = async (_id: string, _domain?: string) => {
    // Currently simulated
    return { success: true, verified: true };
  };

  const addDepartment = async (organization_id: string, name: string, extra?: Partial<DbDepartment>) => {
    const payload = { organization_id, name, ...extra };
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/departments', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        await fetchHierarchy();
        return data;
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to add department');
    } catch (err: any) {
      if (supabase) {
        const { data, error } = await supabase.from('departments').insert(payload).select().single();
        if (error) throw error;
        await fetchHierarchy();
        return data;
      }
      throw err;
    }
  };

  const updateDepartment = async (id: string, name: string, extra?: Partial<DbDepartment>) => {
    const payload = { name, ...extra };
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/departments/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        await fetchHierarchy();
        return data;
      }
    } catch {}
    if (!supabase) return null;
    const { data, error } = await supabase.from('departments').update(payload).eq('id', id).select().single();
    if (error) throw error;
    await fetchHierarchy();
    return data;
  };

  const deleteDepartment = async (id: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/departments/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        await fetchHierarchy();
        return;
      }
    } catch {}
    if (supabase) {
      const { error } = await supabase.from('departments').delete().eq('id', id);
      if (error) throw error;
      await fetchHierarchy();
    }
  };

  const addTeam = async (organization_id: string, department_id: string | null, name: string, extra?: Partial<DbTeam>) => {
    const payload = { organization_id, department_id, name, ...extra };
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/teams', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        await fetchHierarchy();
        return data;
      }
    } catch {}
    if (!supabase) return null;
    const { data, error } = await supabase.from('teams').insert(payload).select().single();
    if (error) throw error;
    await fetchHierarchy();
    return data;
  };

  const updateTeam = async (id: string, name: string, department_id: string | null, extra?: Partial<DbTeam>) => {
    const payload = { name, department_id, ...extra };
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/teams/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        await fetchHierarchy();
        return data;
      }
    } catch {}
    if (!supabase) return null;
    const { data, error } = await supabase.from('teams').update(payload).eq('id', id).select().single();
    if (error) throw error;
    await fetchHierarchy();
    return data;
  };

  const deleteTeam = async (id: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/teams/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        await fetchHierarchy();
        return;
      }
    } catch {}
    if (!supabase) return;
    const { error } = await supabase.from('teams').delete().eq('id', id);
    if (error) throw error;
    await fetchHierarchy();
  };

  const getTeamMembers = async (teamId: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/teams/${encodeURIComponent(teamId)}/members`, { headers });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      console.warn('Failed to fetch team members via API:', e);
    }
    return [];
  };

  const addTeamMember = async (teamId: string, userId: string, isPrimary = false, roleInTeam = 'MEMBER') => {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/teams/${encodeURIComponent(teamId)}/members`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_id: userId, is_primary: isPrimary, role_in_team: roleInTeam })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to add team member');
    }
    return await res.json();
  };

  const removeTeamMember = async (teamId: string, userId: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to remove team member');
    }
    return await res.json();
  };

  return {
    organizations,
    departments,
    teams,
    loading,
    refresh: fetchHierarchy,
    addOrganization,
    updateOrganization,
    deleteOrganization,
    verifyDomain,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    addTeam,
    updateTeam,
    deleteTeam,
    getTeamMembers,
    addTeamMember,
    removeTeamMember
  };
}


